/**
 * Footing (02 §4: "on ice: low traction, 0.3 s direction lag"). On firm ground the ranger
 * goes exactly where the stick says. On ice his direction of travel only eases toward it, so
 * he cannot turn on the spot and slides a little when he lets go. The direction he is
 * actually travelling lives in `world.hazards.slideX / slideZ`, so a save mid-skid resumes mid-skid.
 */

import type { Ctx } from '../ctx';
import { HAZARDS } from '../tuning';
import { DT } from '../types';

/** Below this the slide has spent itself. */
const REST = 0.02;

/**
 * The direction (at most unit length) he moves this tick, given the commanded one.
 * `onIce` false: the command, unchanged - and the slide is kept equal to it, so stepping onto
 * ice carries his heading on.
 */
export function tractionDirection(ctx: Ctx, mx: number, mz: number, onIce: boolean): { x: number; z: number } {
  const h = ctx.world.hazards;
  if (!onIce) {
    h.slideX = mx;
    h.slideZ = mz;
    return { x: mx, z: mz };
  }
  if (h.slideX === 0 && h.slideZ === 0) {
    // A direction lag, not a speed limit: from a standstill he sets off exactly as commanded.
    h.slideX = mx;
    h.slideZ = mz;
    return { x: mx, z: mz };
  }
  // First-order lag with time constant `iceLagSeconds`, stepped implicitly (stable at any tick rate).
  // A reversal passes close to zero and carries on easing; only a released stick lets the slide die.
  const k = DT / (HAZARDS.iceLagSeconds + DT);
  h.slideX += (mx - h.slideX) * k;
  h.slideZ += (mz - h.slideZ) * k;
  if (mx === 0 && mz === 0 && h.slideX * h.slideX + h.slideZ * h.slideZ < REST * REST) {
    h.slideX = 0;
    h.slideZ = 0;
  }
  return { x: h.slideX, z: h.slideZ };
}
